import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, ExternalLink, RotateCcw, Loader2, Building2, Users, BarChart3, Home, PenLine, List, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

import { BACKEND_URL } from '../../config';
const API = BACKEND_URL;

const VERSIONS = [
  {
    id: 'solo',
    label: 'Solo Demo',
    desc: 'Single-tenant claims management',
    color: 'teal',
    ring: 'ring-teal-500/30',
    bg: 'bg-teal-500/10',
    text: 'text-teal-400',
    btn: 'bg-teal-600 hover:bg-teal-700',
    roles: ['admin', 'manager', 'member', 'viewer'],
    links: [
      { label: 'Home', path: '/', icon: Home },
      { label: 'Intake', path: '/intake', icon: PenLine },
      { label: 'All Entries', path: '/all-entries', icon: List },
      { label: 'Company Entries', path: '/companies', icon: Building2 },
      { label: 'Settings', path: '/settings', icon: Settings },
    ],
  },
  {
    id: 'provider',
    label: 'Provider Demo',
    desc: 'Multi-org provider dashboard',
    color: 'blue',
    ring: 'ring-blue-500/30',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    btn: 'bg-blue-600 hover:bg-blue-700',
    roles: ['admin', 'manager', 'member', 'viewer'],
    links: [
      { label: 'Home', path: '/', icon: Home },
      { label: 'Organizations', path: '/organizations', icon: BarChart3 },
      { label: 'Intake', path: '/intake', icon: PenLine },
      { label: 'All Entries', path: '/all-entries', icon: List },
      { label: 'Settings', path: '/settings', icon: Settings },
    ],
  },
  {
    id: 'operator',
    label: 'Platform Operator',
    desc: 'System-wide admin & Dev Hub',
    color: 'purple',
    ring: 'ring-purple-500/30',
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    btn: 'bg-purple-600 hover:bg-purple-700',
    roles: ['super_admin', 'admin', 'manager', 'member'],
    links: [
      { label: 'Home', path: '/', icon: Home },
      { label: 'Dev Hub', path: '/dev-hub', icon: Zap },
      { label: 'User Mgmt', path: '/user-management', icon: Users },
      { label: 'Settings', path: '/settings', icon: Settings },
    ],
  },
];

export default function DevQuickAccess() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState({ solo: 'admin', provider: 'admin', operator: 'super_admin' });
  const [launching, setLaunching] = useState(null);
  const [resetting, setResetting] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/dev/quick-access/stats`, { withCredentials: true });
      setStats(res.data);
    } catch { toast.error('Failed to load stats'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const launch = async (versionId, targetPath = '/') => {
    setLaunching(versionId);
    try {
      const res = await axios.post(`${API}/api/dev/quick-access/login`, {
        version: versionId,
        role: roles[versionId],
      }, { withCredentials: true });
      const token = res.data?.token;
      if (token) {
        window.open(`${API}/api/dev/quick-access/enter?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(targetPath)}`, '_blank');
        toast.success(`Opened ${versionId} as ${roles[versionId]}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Launch failed');
    }
    setLaunching(null);
  };

  const resetData = async (versionId) => {
    if (!window.confirm(`Reset all demo data for ${versionId}? Claims, docs, and notes will be deleted.`)) return;
    setResetting(versionId);
    try {
      const res = await axios.post(`${API}/api/dev/quick-access/reset`, { version: versionId }, { withCredentials: true });
      toast.success(`${res.data.message}: ${res.data.deleted?.claims || 0} claims removed`);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
    setResetting(null);
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="dev-quick-access-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Zap className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Quick Access</h1>
          <p className="text-xs text-slate-400">Launch any demo version with a specific role in a new tab</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {VERSIONS.map((v) => {
          const s = stats[v.id] || {};
          const isLaunching = launching === v.id;
          const isResetting = resetting === v.id;

          return (
            <div
              key={v.id}
              className={`bg-slate-900 border border-slate-800 rounded-xl ring-1 ${v.ring} overflow-hidden`}
              data-testid={`qa-card-${v.id}`}
            >
              {/* Header */}
              <div className={`${v.bg} px-5 py-4 border-b border-slate-800`}>
                <h2 className={`text-base font-bold ${v.text}`}>{v.label}</h2>
                <p className="text-[11px] text-slate-400 mt-0.5">{v.desc}</p>
              </div>

              {/* Stats */}
              <div className="px-5 py-3 border-b border-slate-800/60">
                {loading ? (
                  <div className="flex items-center gap-2 text-slate-500 text-xs py-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">{s.claims ?? '-'}</p>
                      <p className="text-[10px] text-slate-500">Claims</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{s.companies ?? '-'}</p>
                      <p className="text-[10px] text-slate-500">Companies</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{v.id === 'operator' ? (s.tenants ?? '-') : (s.orgs ?? '-')}</p>
                      <p className="text-[10px] text-slate-500">{v.id === 'operator' ? 'Tenants' : 'Orgs'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* User & Subscription info */}
              <div className="px-5 py-2.5 border-b border-slate-800/60 flex items-center justify-between text-[11px]">
                <span className="text-slate-500">User: <span className="text-slate-300">{s.user?.name || '-'}</span></span>
                {s.subscription ? (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    s.subscription.status === 'trialing' ? 'bg-blue-500/10 text-blue-400' :
                    s.subscription.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
                    'bg-slate-700 text-slate-400'
                  }`}>{s.subscription.status}</span>
                ) : (
                  <span className="text-[10px] text-slate-600">No sub</span>
                )}
              </div>

              {/* View As (Role Picker) */}
              <div className="px-5 py-3 border-b border-slate-800/60">
                <label className="text-[10px] text-slate-500 mb-1.5 block uppercase tracking-wider">View as</label>
                <Select value={roles[v.id]} onValueChange={(val) => setRoles({ ...roles, [v.id]: val })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 h-8 text-xs" data-testid={`qa-role-${v.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {v.roles.map((r) => (
                      <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Launch + Reset Buttons */}
              <div className="px-5 py-3 border-b border-slate-800/60 flex items-center gap-2">
                <Button
                  onClick={() => launch(v.id)}
                  disabled={isLaunching}
                  className={`flex-1 ${v.btn} text-white text-xs font-semibold h-9`}
                  data-testid={`qa-launch-${v.id}`}
                >
                  {isLaunching ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <ExternalLink className="w-3 h-3 mr-1.5" />}
                  Launch
                </Button>
                {v.id !== 'operator' && (
                  <Button
                    variant="outline"
                    onClick={() => resetData(v.id)}
                    disabled={isResetting}
                    className="border-slate-700 text-slate-400 hover:text-red-400 hover:border-red-500/50 h-9 px-3"
                    data-testid={`qa-reset-${v.id}`}
                  >
                    {isResetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  </Button>
                )}
              </div>

              {/* Quick Links */}
              <div className="px-5 py-3">
                <label className="text-[10px] text-slate-500 mb-2 block uppercase tracking-wider">Quick Links</label>
                <div className="flex flex-wrap gap-1.5">
                  {v.links.map((link) => (
                    <button
                      key={link.path}
                      onClick={() => launch(v.id, link.path)}
                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                      data-testid={`qa-link-${v.id}-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <link.icon className="w-3 h-3" />
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
